import { useState } from 'react'
import Select from 'react-select'
import { Loading } from 'notiflix'
import { Report } from 'notiflix/build/notiflix-report-aio'
import DatePicker from 'react-datepicker'
import { registerLocale } from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import { enGB } from 'date-fns/locale'
import api from '../../helpers/apiRequester'
import styles from './styles.module.scss'

registerLocale('en-GB', enGB)

interface FilterOption {
    label: string
    value: string
}

interface AgentTeamFilters {
    /** Array of { label, value } options for agent dropdown. value is matched against the data row's `agent` column case-insensitively. */
    agents?: FilterOption[]
    /** Map of teamName -> agent identifiers (names/emails) belonging to that team. */
    teamsAgents?: Record<string, string[]>
    /** Map of agentName (lower-cased) -> teamName. Used to inject a team column into every downloaded CSV. */
    agentToTeam?: Record<string, string>
    /** Which dataset (sent or signed) to pull when filtering — corresponds to the reportType. */
    filterDataset?: 'sent' | 'signed'
}

interface ReportDownloadProps {
    reportType: string
    reportTitle?: string
    filters?: AgentTeamFilters
}

const ReportDownload = ({ reportType, reportTitle = 'Download Report', filters }: ReportDownloadProps) => {
    const [isExpanded, setIsExpanded] = useState(false)
    const [fromDate, setFromDate] = useState<Date | null>(null)
    const [toDate, setToDate] = useState<Date | null>(null)
    const [isLoading, setIsLoading] = useState(false)
    const [agentFilter, setAgentFilter] = useState<string>('all')
    const [teamFilter, setTeamFilter] = useState<string>('all')

    const formatDateToISO = (date: Date): string => {
        const utcDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))
        return utcDate.toISOString()
    }

    const validateDates = (): boolean => {
        if (!fromDate || !toDate) {
            Report.failure('Error', 'Please select both from and to dates', 'OK')
            return false
        }

        if (fromDate > toDate) {
            Report.failure('Error', 'From date cannot be later than to date', 'OK')
            return false
        }

        return true
    }

    // Agent column index in each report CSV (0-based).
    // Signatures:  case reference | client_id | status | signature date | agent name | client name
    // Links sent:  ID | agent | client_id | case_ref | token | send_type | recipient | reused | sent_datetime
    const AGENT_COL_INDEX: Record<string, number> = { signed: 4, sent: 1 }

    // Split a single CSV row into columns, respecting quoted fields.
    const parseCols = (row: string): string[] => {
        const cols: string[] = []
        let cur = ''
        let inQuote = false
        for (let i = 0; i < row.length; i++) {
            const ch = row[i]
            if (ch === '"') { inQuote = !inQuote; continue }
            if (ch === ',' && !inQuote) { cols.push(cur); cur = ''; continue }
            cur += ch
        }
        cols.push(cur)
        return cols
    }

    const escapeCsv = (v: string) =>
        v.includes(',') || v.includes('"') || v.includes('\n') ? `"${v.replace(/"/g, '""')}"` : v

    // Insert a value at `insertAfter` index in a parsed columns array and rejoin as CSV.
    const insertCol = (cols: string[], insertAfter: number, value: string): string => {
        const out = [...cols]
        out.splice(insertAfter + 1, 0, value)
        return out.map(escapeCsv).join(',')
    }

    const processAndDownload = async (isFullReport: boolean) => {
        if (!filters?.filterDataset) return

        const dataset = filters.filterDataset
        const agentColIdx = AGENT_COL_INDEX[dataset] ?? 1

        // Resolve agent scope filter (null = no filter, show all).
        let scopedAgents: Set<string> | null = null
        if (agentFilter !== 'all') {
            scopedAgents = new Set([agentFilter.toLowerCase()])
        } else if (teamFilter !== 'all') {
            const teamAgentList = filters.teamsAgents?.[teamFilter] || []
            scopedAgents = new Set(teamAgentList.map((a) => a.toLowerCase()))
        }

        // 1. Get the full report from the backend.
        const payload: any = { reportType, isFullReport }
        if (!isFullReport) {
            payload.fromDate = formatDateToISO(fromDate!)
            payload.toDate = formatDateToISO(toDate!)
        }
        const response = await api({ url: '/download-report', data: payload })
        const fileUrl = response.data?.fileUrl
        if (!fileUrl) throw new Error('No file URL received from server')

        // 2. Fetch the CSV text from the signed S3 URL.
        const csvText = await fetch(fileUrl).then((r) => r.text())
        const allRows = csvText.split('\n').filter((r) => r.trim())
        const headerCols = parseCols(allRows[0])
        const dataRows = allRows.slice(1)

        // 3. Inject team column header after agent column.
        const hasTeamMap = !!filters.agentToTeam && Object.keys(filters.agentToTeam).length > 0
        const newHeader = hasTeamMap
            ? insertCol(headerCols, agentColIdx, 'team')
            : allRows[0]

        // 4. Process data rows: optionally filter by scope, always inject team.
        const processedRows: string[] = []
        for (const row of dataRows) {
            const cols = parseCols(row)
            const agentVal = (cols[agentColIdx] ?? '').trim().toLowerCase()

            if (scopedAgents && !scopedAgents.has(agentVal)) continue

            if (hasTeamMap) {
                const teamName = filters.agentToTeam![agentVal] || 'Unassigned'
                processedRows.push(insertCol(cols, agentColIdx, teamName))
            } else {
                processedRows.push(row)
            }
        }

        if (!processedRows.length) {
            Report.warning('No rows', 'No data matched the selected filters.', 'OK')
            return
        }

        // 5. Build filename and trigger download.
        const scopeLabel = agentFilter !== 'all'
            ? `agent-${agentFilter.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`
            : teamFilter !== 'all'
                ? `team-${teamFilter.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`
                : 'all'
        const filename = `pcp-referral-${dataset}-${scopeLabel}-${new Date().toISOString().slice(0, 10)}.csv`

        const csv = [newHeader, ...processedRows].join('\n')
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = filename
        a.click()
        URL.revokeObjectURL(url)

        Report.success('Success', `Report generated with ${processedRows.length} row${processedRows.length === 1 ? '' : 's'}.`, 'OK')
    }

    const downloadReport = async (isFullReport = false) => {
        if (!isFullReport && !validateDates()) return

        setIsLoading(true)
        Loading.standard(isFullReport ? 'Generating full report...' : 'Generating report...')

        try {
            // When filters are provided (engagement stats page), always go through
            // the processing path so the team column is injected into every download.
            if (filters?.filterDataset) {
                await processAndDownload(isFullReport)
                return
            }

            // Default path for report sections without filters — open S3 URL directly.
            const payload: any = { reportType, isFullReport }
            if (!isFullReport) {
                payload.fromDate = formatDateToISO(fromDate!)
                payload.toDate = formatDateToISO(toDate!)
            }
            const response = await api({ url: '/download-report', data: payload })
            if (response.data?.fileUrl) {
                window.open(response.data.fileUrl, '_blank')
                Report.success('Success', isFullReport ? 'Full report generated successfully!' : 'Report generated successfully!', 'OK')
            } else {
                Report.failure('Error', 'No file URL received from server', 'OK')
            }
        } catch (error: any) {
            console.error('Error downloading report:', error)
            Report.failure('Error', error.response?.data?.message || 'Failed to generate report. Please try again.', 'OK')
        } finally {
            setIsLoading(false)
            Loading.remove()
        }
    }

    const handleDownloadReport = () => downloadReport(false)
    const handleDownloadFullReport = () => downloadReport(true)

    const onAgentChange = (v: string) => {
        setAgentFilter(v)
        if (v !== 'all') setTeamFilter('all')
    }
    const onTeamChange = (v: string) => {
        setTeamFilter(v)
        if (v !== 'all') setAgentFilter('all')
    }

    return (
        <div className={styles.reportDownloadContainer}>
            <button
                className={styles.collapseToggle}
                onClick={() => setIsExpanded((v) => !v)}
                aria-expanded={isExpanded}
            >
                <span>{reportTitle}</span>
                <span className={`${styles.chevron} ${isExpanded ? styles.chevronOpen : ''}`}>&#8250;</span>
            </button>

            <div className={`${styles.formContainer} ${isExpanded ? styles.formContainerOpen : ''}`}>
                <div className={styles.dateInputGroup}>
                    <div className={styles.dateInput}>
                        <label htmlFor="fromDate">From Date:</label>
                        <DatePicker
                            id="fromDate"
                            selected={fromDate}
                            onChange={(date) => setFromDate(date)}
                            dateFormat="dd/MM/yyyy"
                            dropdownMode="select"
                            showMonthDropdown
                            showYearDropdown
                            className={styles.datePicker}
                            placeholderText="Select from date"
                            locale={enGB}
                            maxDate={toDate || undefined}
                        />
                    </div>

                    <div className={styles.dateInput}>
                        <label htmlFor="toDate">To Date:</label>
                        <DatePicker
                            id="toDate"
                            selected={toDate}
                            onChange={(date) => setToDate(date)}
                            dateFormat="dd/MM/yyyy"
                            dropdownMode="select"
                            showMonthDropdown
                            showYearDropdown
                            className={styles.datePicker}
                            placeholderText="Select to date"
                            locale={enGB}
                            minDate={fromDate || undefined}
                        />
                    </div>
                </div>

                {filters?.filterDataset && (
                    <div className={styles.dateInputGroup}>
                        {filters.agents && filters.agents.length > 0 && (
                            <div className={styles.dateInput}>
                                <label>Agent:</label>
                                <Select
                                    inputId="agentFilter"
                                    options={[{ label: 'All agents', value: 'all' }, ...filters.agents]}
                                    value={
                                        agentFilter === 'all'
                                            ? { label: 'All agents', value: 'all' }
                                            : filters.agents.find((a) => a.value === agentFilter) ?? null
                                    }
                                    onChange={(opt) => onAgentChange(opt?.value ?? 'all')}
                                    isSearchable
                                    placeholder="Search agents..."
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                    styles={{
                                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                        control: (base) => ({
                                            ...base,
                                            padding: '4px 4px',
                                            border: '2px solid #e1e5e9',
                                            borderRadius: '8px',
                                            fontSize: '15px',
                                            boxShadow: 'none',
                                            '&:hover': { borderColor: 'var(--brand-primary)' },
                                        }),
                                        option: (base, state) => ({
                                            ...base,
                                            backgroundColor: state.isSelected ? '#d48958' : state.isFocused ? '#f9f0e8' : 'white',
                                            color: state.isSelected ? '#fff' : '#333',
                                        }),
                                    }}
                                />
                            </div>
                        )}

                        {filters.teamsAgents !== undefined && (
                            <div className={styles.dateInput}>
                                <label htmlFor="teamFilter">Team:</label>
                                <select
                                    id="teamFilter"
                                    className={styles.datePicker}
                                    value={teamFilter}
                                    onChange={(e) => onTeamChange(e.target.value)}
                                >
                                    <option value="all">All teams</option>
                                    {Object.keys(filters.teamsAgents).sort().map((t) => (
                                        <option key={t} value={t}>
                                            {t}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        )}
                    </div>
                )}

                <div className={styles.buttonGroup}>
                    <button className={styles.downloadButton} onClick={handleDownloadReport} disabled={isLoading}>
                        {isLoading ? 'Generating Report...' : 'Download Report'}
                    </button>

                    <button className={styles.fullReportButton} onClick={handleDownloadFullReport} disabled={isLoading}>
                        {isLoading ? 'Generating Report...' : 'Download Full Report'}
                    </button>
                </div>
            </div>
        </div>
    )
}

export default ReportDownload
